/* eslint react-hooks/exhaustive-deps: "warn" */
import { type KeyboardEvent, type ReactNode, useRef, useState } from 'react';

import { useInt } from 'hooks/useInt';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';

import styles from './ManufacturingTeams.module.scss';

type Translate = (key: string) => string;

type Team = {
  number: string;
  tab: string;
  idLabel: string;
  title: string;
  ask: string;
  ret: ReactNode;
};

const getTeams = (t: Translate): Team[] => [
  {
    number: '01',
    tab: t('Procurement'),
    idLabel: t('01 · procurement'),
    title: t('Procurement'),
    ask: t('Is this a real company, and is it the one we think it is?'),
    ret: (
      <>
        {t('Registry-verified legal identity before the vendor record is created in')} <b>SAP</b>, <b>Oracle</b>{' '}
        {t('or')} <b>Coupa</b>
        {t(', with the registration number written back to the master.')}
      </>
    ),
  },
  {
    number: '02',
    tab: t('Trade compliance'),
    idLabel: t('02 · trade compliance'),
    title: t('Trade compliance'),
    ask: t('Who ultimately owns this supplier, and are they restricted?'),
    ret: (
      <>
        {t('Ownership walked to the ultimate parent, then screened at entity, officer and owner level, with the')}{' '}
        <b>{t('source list and version date')}</b> {t('recorded against the result.')}
      </>
    ),
  },
  {
    number: '03',
    tab: t('Supply chain risk'),
    idLabel: t('03 · supply chain risk'),
    title: t('Supply chain risk'),
    ask: t('Which of our suppliers moved this week?'),
    ret: (
      <>
        {t(
          'A monitored portfolio returning insolvency filings, charge registrations, officer changes and dissolutions as',
        )}{' '}
        <b>{t('dated events')}</b>
        {t(', not as a refreshed score.')}
      </>
    ),
  },
  {
    number: '04',
    tab: t('Master data'),
    idLabel: t('04 · master data'),
    title: t('Master data'),
    ask: t('How many of these 40,000 vendor rows are the same company?'),
    ret: (
      <>
        {t('Batch matching against registration numbers, collapsing duplicates and linking every subsidiary to its')}{' '}
        <b>{t('corporate group')}</b> {t('for spend consolidation.')}
      </>
    ),
  },
  {
    number: '05',
    tab: t('Finance & credit'),
    idLabel: t('05 · finance & credit'),
    title: t('Finance & credit'),
    ask: t('Can this supplier fund the tooling we are about to order?'),
    ret: (
      <>
        <b>{t('Filed accounts')}</b>{' '}
        {t(
          'as submitted to the registry, with turnover, assets, liabilities and filing dates, alongside the group structure the accounts sit in.',
        )}
      </>
    ),
  },
  {
    number: '06',
    tab: t('Sales & channel'),
    idLabel: t('06 · sales & channel'),
    title: t('Sales & channel'),
    ask: t('Which manufacturers in this region should we be selling into?'),
    ret: (
      <>
        {t('Segmentation on')} <b>{t('registry activity codes')}</b>
        {t(', legal form, incorporation date, employee bands and registered location, exported to your CRM.')}
      </>
    ),
  },
];

export const ManufacturingTeams = () => {
  const { t } = useInt();
  const teams = getTeams(t);
  const [active, setActive] = useState(0);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const pick = (index: number) => {
    setActive(index);
    tabRefs.current[index]?.focus();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    let next: number | null = null;
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      next = (index + 1) % teams.length;
    }
    if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      next = (index - 1 + teams.length) % teams.length;
    }
    if (next !== null) {
      event.preventDefault();
      pick(next);
    }
  };

  const team = teams[active];

  return (
    <section className={styles.root}>
      <div className={styles.grid}>
        <div>
          <TitleBlock
            extra={t('Who uses it')}
            title={t('One register, read six different ways.')}
            description={t(
              'The same resolved entity record answers a different question depending on which desk is asking. Nobody re-keys it, and nobody argues about whose list is correct.',
            )}
            align="left"
          />

          <div className={styles.list} role="tablist" aria-label="Teams">
            {teams.map((item, i) => (
              <button
                type="button"
                role="tab"
                aria-selected={active === i}
                id={`mt-tab-${i}`}
                aria-controls={`mt-pane-${i}`}
                className={styles.tab}
                ref={(el) => {
                  tabRefs.current[i] = el;
                }}
                onClick={() => pick(i)}
                onKeyDown={(event) => onKeyDown(event, i)}
                key={item.tab}
              >
                <span className={styles.n}>{item.number}</span>
                <span className={styles.t}>{item.tab}</span>
                <span className={styles.ar}>&rarr;</span>
              </button>
            ))}
          </div>
        </div>

        <div className={styles.cardWrap}>
          <div
            className={styles.card}
            id={`mt-pane-${active}`}
            role="tabpanel"
            aria-labelledby={`mt-tab-${active}`}
            key={active}
          >
            <div className={styles.id}>{team.idLabel}</div>
            <h3 className={styles.cardTitle}>{team.title}</h3>

            <div className={styles.field}>
              <span className={styles.lab}>{t('what they ask')}</span>
              <p>{team.ask}</p>
            </div>

            <div className={styles.field}>
              <span className={styles.lab}>{t('what comes back')}</span>
              <p>{team.ret}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
